'use client';

import React, { useState, useEffect, useCallback } from 'react';
import OfferDialog, { OFFER_SUBSCRIBED_KEY, useShopperAudience, type PopupCoupon } from '@/components/ecommerce/OfferDialog';
import {
  claimPromoSlot,
  releasePromoSlot,
  isOrderFlowRoute,
  popupStorageGet,
  popupStorageSet,
  popupStorageRemove,
} from '@/lib/ui/popupQueue';

// Owner id for the single shared promotional-popup slot. This popup previously
// sat outside the queue entirely, so it could appear stacked on top of the
// email-capture or local-appointment popup.
const POPUP_ID = 'exit-intent';

/**
 * Exit Intent Popup Component
 *
 * Shows the email-for-a-discount offer when a visitor looks like leaving.
 * Triggers after 5 seconds on the page when mouse moves toward browser close button.
 *
 * Since 2026-10-21 it shows the same the reference store-style dialog as the timed
 * popup (OfferDialog) and asks for an email before giving the code. It used to
 * show the code straight away on a red "Wait! Don't Leave Yet!" card with a
 * 30-minute countdown that reset on every visit — the countdown was not real.
 * Visitors who already signed up (either popup) never see it.
 *
 * The coupon is fetched dynamically from the API:
 * GET /api/v2/commerce/homepage-popup-coupon
 *
 * To configure a coupon for the popup:
 * 1. Go to Vendor Dashboard > Discounts & Coupons > Coupons tab
 * 2. Create a new coupon or edit an existing one
 * 3. Check "Show in Homepage Popup" checkbox
 * 4. Only ONE coupon can be shown - the most recent active one with this flag
 */
interface ExitIntentPopupProps {
  // Optional props to override API values (for testing or static usage)
  discountCode?: string;
  discountPercent?: number;
}

const ExitIntentPopup: React.FC<ExitIntentPopupProps> = ({
  discountCode: propDiscountCode,
  discountPercent: propDiscountPercent,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  // Guests and signed-in customers only; null until the sign-in check is done.
  const shopper = useShopperAudience();
  const [isReady, setIsReady] = useState(false);
  const [coupon, setCoupon] = useState<PopupCoupon | null>(null);
  const [couponLoaded, setCouponLoaded] = useState(false);
  const [isEnabled, setIsEnabled] = useState<boolean | null>(null);

  // Use props if provided, otherwise use API data
  const discountCode = propDiscountCode || coupon?.code || 'SAVE10';
  const discountPercent = propDiscountPercent || coupon?.discountPercent || coupon?.discountValue || 10;

  // Fetch popup settings to check if enabled
  useEffect(() => {
    // Skip for search engine crawlers
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('googlebot') || ua.includes('bingbot') || ua.includes('yandex') || ua.includes('baiduspider')) {
      setIsEnabled(false);
      return;
    }
    const fetchPopupSettings = async () => {
      try {
        const response = await fetch('/api/v2/commerce/popup-settings');
        const data = await response.json();
        if (data.success && data.data) {
          setIsEnabled(data.data.enable_exit_intent_popup ?? true);
        } else {
          setIsEnabled(true); // Default to enabled
        }
      } catch (error) {
        console.error('Failed to fetch popup settings:', error);
        setIsEnabled(true); // Default to enabled on error
      }
    };

    fetchPopupSettings();
  }, []);

  // Fetch coupon from API
  useEffect(() => {
    // Don't fetch coupon if popup is disabled
    if (isEnabled === false) {
      setCouponLoaded(true);
      return;
    }

    const fetchCoupon = async () => {
      try {
        const response = await fetch('/api/v2/commerce/homepage-popup-coupon');
        const data = await response.json();

        if (data.success && data.data?.coupon) {
          setCoupon(data.data.coupon);
        }
      } catch (error) {
        console.error('Failed to fetch homepage popup coupon:', error);
      } finally {
        setCouponLoaded(true);
      }
    };

    // Only fetch if no props were provided
    if (!propDiscountCode && !propDiscountPercent) {
      fetchCoupon();
    } else {
      setCouponLoaded(true);
    }
  }, [propDiscountCode, propDiscountPercent, isEnabled]);

  // Check if popup has been shown recently.
  //
  // Reads through popupStorageGet, which falls back to an in-memory mirror when
  // localStorage is unavailable (Safari private mode, blocked site data, the
  // Facebook/Instagram in-app browser). Previously this called
  // localStorage.getItem directly: when that threw or failed to persist, the
  // guard read "never shown" forever, and the mobile dwell timer below re-armed
  // on every close — so the popup returned every 45 seconds and the visitor
  // could not get past it. That is the reported complaint.
  const hasShownRecently = useCallback(() => {
    if (typeof window === 'undefined') return true;
    // Already signed up through either popup: they have the code.
    if (popupStorageGet(OFFER_SUBSCRIBED_KEY)) return true;
    const lastShown = popupStorageGet('exit-intent-shown');
    if (!lastShown) return false;
    const hoursSinceShown = (Date.now() - parseInt(lastShown)) / (1000 * 60 * 60);
    return hoursSinceShown < 24; // Don't show again for 24 hours
  }, []);

  // Add delay before enabling exit intent detection
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 5000); // Wait 5 seconds before enabling exit intent

    return () => clearTimeout(timer);
  }, []);

  // Exit intent detection
  useEffect(() => {
    // Don't enable if popup is disabled in admin settings
    if (isEnabled === false) return;
    // Staff (admin, vendor, dealer, installer…) never get the offer.
    if (shopper !== true) return;
    if (!isReady || !couponLoaded || hasShownRecently()) return;

    // Never interrupt someone configuring or paying for an order. The mobile
    // dwell trigger below fires after 45s on the page, and the configurator
    // routinely takes minutes (measuring, fabric, options) — so without this
    // the popup landed squarely on top of the buying flow.
    if (isOrderFlowRoute()) return;

    // Don't enable if no coupon is available (unless props were provided)
    if (!propDiscountCode && !coupon) return;

    let lastY = 0;

    // Single guarded entry point so every trigger dedupes the same way.
    // The timestamp is written BEFORE showing, and through the mirrored store,
    // so the "once per 24h" guard holds even if nothing can be persisted.
    const showPopup = () => {
      if (isVisible || hasShownRecently()) return;
      if (isOrderFlowRoute()) return; // route can change without a remount
      if (!claimPromoSlot(POPUP_ID)) return; // never stack on another popup
      popupStorageSet('exit-intent-shown', Date.now().toString());
      setIsVisible(true);
    };

    // --- Desktop: mouse leaving toward the top (browser close/tab) ---
    const handleMouseMove = (e: MouseEvent) => {
      lastY = e.clientY;
    };

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 5 && lastY > e.clientY) {
        showPopup();
      }
    };

    // Tab hidden → returned (works on both desktop and mobile).
    const handleVisibilityChange = () => {
      if (!isReady) return;

      if (document.visibilityState === 'hidden' && !hasShownRecently()) {
        popupStorageSet('exit-intent-pending', 'true');
      } else if (document.visibilityState === 'visible') {
        const pending = popupStorageGet('exit-intent-pending');
        if (pending && !hasShownRecently()) {
          showPopup();
          popupStorageRemove('exit-intent-pending');
        }
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // --- Mobile: phones have no cursor, so the mouse-leave trigger never
    // fires. Catch the two signals a leaving mobile visitor actually produces:
    // a fast upward scroll (reaching for the address bar / back) and, as a
    // fallback, dwell time on the page.
    const isTouch =
      typeof window !== 'undefined' &&
      (window.matchMedia?.('(pointer: coarse)').matches || 'ontouchstart' in window);

    let mobileTimer: ReturnType<typeof setTimeout> | undefined;
    let lastScrollY = window.scrollY;
    let lastScrollT = Date.now();

    const handleScroll = () => {
      const y = window.scrollY;
      const now = Date.now();
      const dy = lastScrollY - y; // positive = scrolling up
      const dt = now - lastScrollT || 1;
      const velocity = dy / dt; // px per ms, upward-positive
      lastScrollY = y;
      lastScrollT = now;
      // Fast flick upward while near the top of the page ≈ intent to leave.
      if (velocity > 1.2 && y < window.innerHeight * 0.6) {
        showPopup();
      }
    };

    if (isTouch) {
      window.addEventListener('scroll', handleScroll, { passive: true });
      // Dwell fallback: if a mobile visitor has browsed a while without any
      // exit signal, make the offer before they drift away.
      mobileTimer = setTimeout(showPopup, 45000);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (isTouch) window.removeEventListener('scroll', handleScroll);
      if (mobileTimer) clearTimeout(mobileTimer);
    };
  }, [isVisible, isReady, couponLoaded, coupon, propDiscountCode, hasShownRecently, isEnabled, shopper]);

  const handleClose = () => {
    // Re-record the dismissal on close as well as on open. Belt and braces: if
    // the open-time write was lost, this is the second chance to make "already
    // seen" stick, and it releases the shared slot so a later popup can use it.
    popupStorageSet('exit-intent-shown', Date.now().toString());
    popupStorageRemove('exit-intent-pending');
    setIsVisible(false);
    releasePromoSlot(POPUP_ID);
  };

  if (!isVisible || shopper !== true) return null;

  return (
    <OfferDialog
      title="Wait! Before You Go,"
      source="exit_intent"
      coupon={coupon}
      fallbackPercent={discountPercent}
      fallbackCode={discountCode}
      onClose={handleClose}
    />
  );
};

export default ExitIntentPopup;
